import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import {
  getClasses,
  getStudentsByClass,
  getActiveTerms,
} from "../api/services";

function SendReportCards() {

  const [classes, setClasses] = useState([]);

  const [students, setStudents] = useState([]);

  const { t } = useTranslation();

  const [terms, setTerms] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");

  const [selectedTerm, setSelectedTerm] = useState("");

  const [sending, setSending] = useState(false);

  const [progress, setProgress] = useState(0);

  const [sentCount, setSentCount] = useState(0);

  const [failedCount, setFailedCount] = useState(0);

  const [feeBlockedCount, setFeeBlockedCount] = useState(0);

  const [logs, setLogs] = useState([]);

  const token = localStorage.getItem("access");

  useEffect(() => {

    fetchInitial();

  }, []);

  // =====================================================
  // FETCH INITIAL
  // =====================================================

  const fetchInitial = async () => {

    try {

      const [classRes, termRes] = await Promise.all([

        getClasses(),

        getActiveTerms(),

      ]);

      setClasses(classRes.data);

      setTerms(termRes.data);

    } catch (err) {

      console.error(err);

      alert("Failed to load initial data.");
    }
  };

  // =====================================================
  // FETCH STUDENTS
  // =====================================================

  const handleClassChange = async (classId) => {

    setSelectedClass(classId);

    setStudents([]);

    if (!classId) return;

    try {

      const res = await getStudentsByClass(
        classId
      );

      setStudents(res.data);

    } catch (err) {

      console.error(err);

      alert("Failed to load students.");
    }
  };

  // =====================================================
  // SEND REPORTS
  // =====================================================

  const sendReports = async () => {

    if (!selectedClass || !selectedTerm) {

      alert(
        "Please select class and term."
      );

      return;
    }

    if (students.length === 0) {

      alert("No students found.");

      return;
    }

    const confirmed = window.confirm(
      "Send report cards to all parents in this class?"
    );

    if (!confirmed) return;

    try {

      setSending(true);

      setProgress(15);

      setSentCount(0);

      setFailedCount(0);

      setFeeBlockedCount(0);

      setLogs([]);

      const res = await axios.post(

        "https://api.eduskot.com/api/reports/send-class-report-cards/",

        {
          class_id: selectedClass,
          term_id: selectedTerm,
        },

        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = res.data;

      setProgress(100);

      setSentCount(data.sent);

      setFailedCount(data.failed);

      setFeeBlockedCount(
        data.fee_blocked
      );

      setLogs(data.logs || []);

      alert(
        "Report card sending completed successfully."
      );

    } catch (err) {

      console.error(err);

      alert(
        err.response?.data?.error ||
        "Failed to send reports."
      );

    } finally {

      setSending(false);
    }
  };

  return (

    <div style={styles.page}>

      {/* ===================================================== */}
      {/* HEADER */}
      {/* ===================================================== */}

      <div style={styles.header}>

        <h1 style={styles.title}>
          {t("parentReportDistribution")}
        </h1>

        <p style={styles.subtitle}>
          {t("parentReportDistributionText")}
        </p>

      </div>

      {/* ===================================================== */}
      {/* MAIN CARD */}
      {/* ===================================================== */}

      <div style={styles.card}>

        {/* CLASS */}

        <div style={styles.field}>

          <label style={styles.label}>
            {t("selectClass")}
          </label>

          <select
            style={styles.select}
            value={selectedClass}
            onChange={(e) =>
              handleClassChange(
                e.target.value
              )
            }
          >

            <option value="">
              -- Select Class --
            </option>

            {classes.map((c) => (

              <option key={c.id} value={c.id}>
    {c["department__name"]
        ? `${c.name} (${c["department__name"]})`
        : c.name}
</option>

            ))}

          </select>

        </div>

        {/* TERM */}

        <div style={styles.field}>

          <label style={styles.label}>
            {t("selectTerm")}
          </label>

          <select
            style={styles.select}
            value={selectedTerm}
            onChange={(e) =>
              setSelectedTerm(
                e.target.value
              )
            }
          >

            <option value="">
              -- Select Term --
            </option>

            {terms.map((t) => (

              <option
                key={t.id}
                value={t.id}
              >
                {t.name} — {t.session__name}
              </option>

            ))}

          </select>

        </div>

        {/* STUDENTS */}

        <div style={styles.studentInfo}>

          <strong>
            {t("studentsFound")}:
          </strong>

          {" "}

          {students.length}

        </div>

        {/* PROGRESS */}

        <div style={styles.progressWrapper}>

          <div
            style={{
              ...styles.progressBar,
              width: `${progress}%`,
            }}
          />

        </div>

        <div style={styles.progressText}>

          {
            sending
              ? t("sendingReports")
              : `${progress}% ${t("completed")}`
          }

        </div>

        {/* STATS */}

        <div style={styles.statsGrid}>

          <div style={styles.successBox}>

            <h3>
              {sentCount}
            </h3>

            <p>
              {t("successfullySent")}
            </p>

          </div>

          <div style={styles.failedBox}>

            <h3>
              {failedCount}
            </h3>

            <p>
              {t("failed")}
            </p>

          </div>

          <div style={styles.warningBox}>

            <h3>
              {feeBlockedCount}
            </h3>

            <p>
              {t("feeBlocked")}
            </p>

          </div>

        </div>

        {/* BUTTON */}

        <button
          style={{
            ...styles.sendBtn,

            opacity:
              sending ? 0.7 : 1,

            cursor:
              sending
                ? "not-allowed"
                : "pointer",
          }}
          onClick={sendReports}
          disabled={sending}
        >

          {
            sending
              ? t("sendingReports")
              : t("startSendingReports")
          }

        </button>

      </div>

      {/* ===================================================== */}
      {/* LOGS */}
      {/* ===================================================== */}

      {logs.length > 0 && (

        <div style={styles.logsCard}>

          <h2 style={styles.logsTitle}>
            {t("deliveryLogs")}
          </h2>

          <div style={styles.logsContainer}>

            {logs.map((log, index) => (

              <div
                key={index}
                style={styles.logItem}
              >

                <strong>
                  {log.student}
                </strong>

                <span>
                  {log.status}
                </span>

              </div>

            ))}

          </div>

        </div>

      )}

    </div>
  );
}

const styles = {

  page:{
    padding:"7px",
    minHeight:"100vh",
    background:
      "linear-gradient(135deg,#eef2ff,#f8fafc)",
    fontFamily:"Inter, sans-serif"
  },

  header:{
    textAlign:"center",
    marginBottom:"30px"
  },

  title:{
    fontSize:"32px",
    fontWeight:"700",
    color:"#0f172a",
    marginBottom:"10px"
  },

  subtitle:{
    color:"#64748b",
    fontSize:"15px"
  },

  card:{
    width:"100%",
    maxWidth:"800px",
    margin:"0 auto",
    background:"white",
    padding:"35px",
    borderRadius:"20px",
    boxShadow:
      "0 20px 50px rgba(0,0,0,0.08)"
  },

  field:{
    display:"flex",
    flexDirection:"column",
    marginBottom:"22px"
  },

  label:{
    marginBottom:"8px",
    fontWeight:"600",
    color:"#334155"
  },

  select:{
    padding:"14px",
    borderRadius:"12px",
    border:"1px solid #cbd5e1",
    fontSize:"14px"
  },

  studentInfo:{
    marginBottom:"20px",
    color:"#334155",
    fontSize:"15px"
  },

  progressWrapper:{
    width:"100%",
    height:"24px",
    background:"#e2e8f0",
    borderRadius:"999px",
    overflow:"hidden",
    marginBottom:"12px"
  },

  progressBar:{
    height:"100%",
    background:"#2563eb",
    transition:"0.4s ease"
  },

  progressText:{
    marginBottom:"25px",
    color:"#475569",
    fontWeight:"600"
  },

  statsGrid:{
    display:"grid",
    gridTemplateColumns:
      "repeat(auto-fit,minmax(180px,1fr))",
    gap:"15px",
    marginBottom:"30px"
  },

  successBox:{
    background:"#dcfce7",
    padding:"18px",
    borderRadius:"16px",
    textAlign:"center",
    color:"#166534"
  },

  failedBox:{
    background:"#fee2e2",
    padding:"18px",
    borderRadius:"16px",
    textAlign:"center",
    color:"#991b1b"
  },

  warningBox:{
    background:"#fef3c7",
    padding:"18px",
    borderRadius:"16px",
    textAlign:"center",
    color:"#92400e"
  },

  sendBtn:{
    width:"100%",
    padding:"16px",
    background:"#2563eb",
    color:"white",
    border:"none",
    borderRadius:"14px",
    fontSize:"16px",
    fontWeight:"700"
  },

  logsCard:{
    width:"100%",
    maxWidth:"800px",
    margin:"30px auto 0",
    background:"white",
    borderRadius:"20px",
    padding:"30px",
    boxShadow:
      "0 20px 50px rgba(0,0,0,0.08)"
  },

  logsTitle:{
    marginBottom:"20px",
    color:"#0f172a"
  },

  logsContainer:{
    display:"flex",
    flexDirection:"column",
    gap:"12px",
    maxHeight:"400px",
    overflowY:"auto"
  },

  logItem:{
    display:"flex",
    justifyContent:"space-between",
    alignItems:"center",
    padding:"14px",
    border:"1px solid #e2e8f0",
    borderRadius:"12px",
    background:"#f8fafc"
  }
};

export default SendReportCards;